import { getAttendanceDisplayValue } from '@/components/feedbacks/session-feedback-utils';
import { StudentShortLink } from '@/components/join-now/student-short-link';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { EntityTable } from '@/components/table/entity-table';
import { Tooltip } from '@/components/tooltip';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination';
import { stringIfNull } from '@/lib/string-utils';
import { associateBy } from '@airreading/common';
import {
  AdminSession,
  SessionFeedbackWithAssessment,
  studentYearOption,
  toSessionFeedbackCompoundId,
  UserLike,
} from '@airreading/common/data';
import { Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';

import { useMemo } from 'react';

interface SessionStudentsTableProps {
  session: AdminSession;
  feedbacks: SessionFeedbackWithAssessment[];
  isLoading?: boolean;
}

const columnHelper = createColumnHelper<UserLike>();
const buildColumns = (session: AdminSession, feedbacksByStudentId: Map<string, SessionFeedbackWithAssessment>) => {
  return [
    columnHelper.accessor('fullNameOrEmail', {
      id: 'student',
      header: 'Student',
      cell: ({ getValue }) => {
        const displayName = getValue();
        return <div>{displayName}</div>;
      },
    }),
    columnHelper.accessor((row) => (row as UserLike & { shortCode?: string }).shortCode, {
      header: 'Direct join short link',
      id: 'shortLink',
      cell: ({ getValue }) => {
        const shortLink = getValue();
        return <StudentShortLink shortCode={shortLink ?? null} />;
      },
    }),
    columnHelper.display({
      id: 'org',
      header: 'District',
      cell: () => session.school.org.name,
    }),
    columnHelper.display({
      id: 'school',
      header: 'School',
      cell: () => session.school.name,
    }),
    columnHelper.accessor('id', {
      id: 'grades',
      header: 'Grade',
      cell: ({ getValue }) => {
        const grade = session.gradeByStudentId[getValue()];
        return <div>{studentYearOption(stringIfNull(grade))}</div>;
      },
    }),
    columnHelper.accessor('id', {
      id: 'attendance',
      header: 'Attendance',
      cell: ({ getValue }) => {
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);
        if (!feedback) {
          return '-';
        }
        return getAttendanceDisplayValue(feedback);
      },
    }),
    columnHelper.accessor('id', {
      id: 'feedbackStatus',
      header: 'Feedback',
      cell: ({ getValue }) => {
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);
        return (
          <SessionFeedbackStatus
            feedbacks={feedback ? [feedback] : []}
            hoursCanceledBeforeStart={session.hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={session.lesson.shouldSkipFeedback}
          />
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'linkToFeedback',
      header: '',
      cell: ({ getValue }) => {
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);

        if (!feedback) {
          return '-';
        }
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(feedback) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <Tooltip trigger={<PencilIcon size={16} />}>
              <div>View/edit feedback</div>
            </Tooltip>
          </Link>
        );
      },
    }),
  ];
};

export const SessionStudentsTable = ({ session, feedbacks, isLoading = false }: SessionStudentsTableProps) => {
  const feedbacksByStudentId = associateBy(feedbacks, (feedback) => feedback.studentId);
  const columns = useMemo(() => buildColumns(session, feedbacksByStudentId), [session, feedbacksByStudentId]);
  const table = useReactTable({
    data: session.students,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return <EntityTable table={table} emptyState="No students found for this session." isLoading={isLoading} />;
};
